'use client';

import Link from 'next/link';
import type { Route } from 'next';
import { signOut } from 'next-auth/react';
import { BarChart3, LogOut, NotebookPen, Settings as SettingsIcon } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

interface UserMenuProps {
  name: string;
  email: string;
  avatarUrl: string | null;
  /** Which side the dropdown opens on — 'right' when triggered from the rail. */
  side?: 'top' | 'right' | 'bottom' | 'left';
}

export function UserMenu({ name, email, avatarUrl, side = 'bottom' }: UserMenuProps) {
  const initials = name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border border-border bg-surface text-[11px] font-semibold uppercase tracking-wider text-muted-foreground transition duration-fast ease-out-quint hover:border-border-strong hover:text-foreground"
        >
          {avatarUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <span>{initials || '?'}</span>
          )}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side={side}
          align="end"
          sideOffset={10}
          className="z-50 min-w-[15rem] overflow-hidden rounded-xl border border-hairline bg-surface p-1.5 shadow-lg animate-fade-in"
        >
          <div className="px-2.5 py-2.5">
            <p className="truncate text-sm font-medium">{name}</p>
            <p className="truncate text-xs text-muted-foreground">{email}</p>
          </div>
          <div className="hairline my-1" />
          <DropdownMenu.Item asChild>
            <Link
              href={'/analytics' as Route}
              className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm outline-none transition duration-fast ease-out-quint focus:bg-surface-muted"
            >
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              Analytics
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <Link
              href={'/notes' as Route}
              className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm outline-none transition duration-fast ease-out-quint focus:bg-surface-muted"
            >
              <NotebookPen className="h-4 w-4 text-muted-foreground" />
              Notes
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <Link
              href="/settings"
              className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm outline-none transition duration-fast ease-out-quint focus:bg-surface-muted"
            >
              <SettingsIcon className="h-4 w-4 text-muted-foreground" />
              Settings
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => signOut({ callbackUrl: '/login' })}
            className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm outline-none transition duration-fast ease-out-quint focus:bg-surface-muted"
          >
            <LogOut className="h-4 w-4 text-muted-foreground" />
            Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
